import React from 'react';
import Dialog from '@dashboard/components/Dialog';
import Button from '@dashboard/components/Button';
import Link from '@dashboard/components/Link/Link';
import { format } from 'date-fns';
import { toast } from '@dashboard/context/ToastContext';
import { Badge } from '@dashboard/components/ui/badge';
import { upperCase } from 'lodash';
import { useAdminStatisticLogs } from '@dashboard/hooks/queries/useAdminQueries';
import { useDeleteStatisticLogs } from '@dashboard/hooks/queries/useAdminMutations';

export default function StatisticLogs() {
  // Use centralized hooks for each status type
  const { data: pendingData } = useAdminStatisticLogs({ status: 'pending', limit: 5 });
  const { data: processingData } = useAdminStatisticLogs({ status: 'processing', limit: 5 });
  const { data: failedData } = useAdminStatisticLogs({ status: 'failed', limit: 5 });
  const { data: completedData } = useAdminStatisticLogs({ status: 'completed', limit: 5 });

  const pendingLogs = pendingData?.data;
  const totalPendingLogs = pendingData?.total;
  const processingLogs = processingData?.data;
  const totalProcessingLogs = processingData?.total;
  const failedLogs = failedData?.data;
  const totalFailedLogs = failedData?.total;
  const completedLogs = completedData?.data;
  const totalCompletedLogs = completedData?.total;

  const deleteStatisticLogsMutation = useDeleteStatisticLogs({
    onSuccess: (_result, { status }) => {
      toast({
        title: `Deleted ${status} logs`,
        type: 'success',
      });
    },
    onError: (error, { status }) => {
      toast({
        title: `Error deleting ${status} logs`,
        description: error?.message,
        type: 'error',
      });
    },
  });

  return (
    <div className="">
      <div className="mb-4">
        <h1>Statistic Logs</h1>
      </div>

      <div className="grid 2xl:grid-cols-3 md:grid-cols-2">
        <div className="border-b border-r border-gray-100 dark:border-border p-4 py-8">
          <div>
            <div className="flex flex-row flex-wrap justify-between items-start px-4 mb-2">
              <div className="leading-tight font-semibold text-gray-900 dark:text-foreground pt-[2px] text-sm">
                <div className="text-left mb-2">
                  <div className="">Pending logs</div>
                  <div className="font-normal mt-2">{totalPendingLogs}</div>
                </div>
              </div>

              <div className="mb-auto">
                <Button
                  appearance="secondary"
                  size="small"
                  onClick={() =>
                    deleteStatisticLogsMutation.mutate({
                      status: 'pending',
                    })
                  }
                >
                  Delete pending logs
                </Button>
              </div>
            </div>

            <div className="py-3 px-4">
              <div className="overflow-auto" style={{ height: '384px' }}>
                {pendingLogs?.map(log => (
                  <div className="flex h-14" key={log._id}>
                    <div className="space-y-1">
                      <p className="text-sm">
                        Seller: <Link to={`/admin/merchants/${log.seller}`}>{log.seller}</Link>
                      </p>
                      <p className="text-sm text-gray-700 dark:text-muted-foreground">
                        Date range: {format(new Date(log.startDate), 'yyyy-MM-dd HH:mm')} -{' '}
                        {format(new Date(log.endDate), 'yyyy-MM-dd HH:mm')}
                      </p>
                    </div>
                    <div className="ml-auto font-medium text-right">
                      <div className="space-y-1">
                        <p>{log.job}</p>
                      </div>
                      <div className="flex gap-x-1">
                        {log.metric && (
                          <Badge variant="info" className="first-letter:uppercase">
                            {log.metric}
                          </Badge>
                        )}
                        <Badge variant="info">{upperCase(log.type)}</Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="border-b border-r border-gray-100 dark:border-border p-4 py-8">
          <div>
            <div className="flex flex-row flex-wrap justify-between items-start px-4 mb-2">
              <div className="leading-tight font-semibold text-gray-900 dark:text-foreground pt-[2px] text-sm">
                <div className="text-left mb-2">
                  <div>Processing logs</div>

                  <div className="font-normal mt-2">{totalProcessingLogs}</div>
                </div>
              </div>

              <div className="mb-auto">
                <Button
                  appearance="secondary"
                  size="small"
                  onClick={() =>
                    deleteStatisticLogsMutation.mutate({
                      status: 'processing',
                    })
                  }
                >
                  Delete processing logs
                </Button>
              </div>
            </div>

            <div className="py-3 px-4">
              <div className="overflow-auto" style={{ height: '384px' }}>
                {processingLogs?.map(log => (
                  <div className="flex h-14" key={log._id}>
                    <div className="space-y-1">
                      <p className="text-sm">
                        Seller: <Link to={`/admin/merchants/${log.seller}`}>{log.seller}</Link>
                      </p>
                      <p className="text-sm text-gray-700 dark:text-muted-foreground">
                        Date range: {format(new Date(log.startDate), 'yyyy-MM-dd HH:mm')} -{' '}
                        {format(new Date(log.endDate), 'yyyy-MM-dd HH:mm')}
                      </p>
                    </div>
                    <div className="ml-auto font-medium text-right">
                      <div className="space-y-1">
                        <p>{log.job}</p>
                      </div>
                      <div className="flex gap-x-1">
                        {log.metric && (
                          <Badge variant="info" className="first-letter:uppercase">
                            {log.metric}
                          </Badge>
                        )}
                        <Badge variant="info">{upperCase(log.type)}</Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="border-b border-r border-gray-100 dark:border-border p-4 py-8">
          <div>
            <div className="flex flex-row flex-wrap justify-between items-start px-4 mb-2">
              <div className="leading-tight font-semibold text-gray-900 dark:text-foreground pt-[2px] text-sm">
                <div className="text-left mb-2">
                  <div>Failed logs</div>

                  <div className="font-normal mt-2">{totalFailedLogs}</div>
                </div>
              </div>

              <div className="mb-auto">
                <Button
                  appearance="secondary"
                  size="small"
                  onClick={() =>
                    deleteStatisticLogsMutation.mutate({
                      status: 'failed',
                    })
                  }
                >
                  Delete failed logs
                </Button>
              </div>
            </div>

            <div className="py-3 px-4">
              <div className="overflow-auto" style={{ height: '384px' }}>
                {failedLogs?.map(log => (
                  <div key={log._id} className="mb-4">
                    <div className="flex">
                      <div className="space-y-1">
                        <p className="text-sm">
                          Seller: <Link to={`/admin/merchants/${log.seller}`}>{log.seller}</Link>
                        </p>
                        <p className="text-sm text-gray-700 dark:text-muted-foreground">
                          Date range: {format(new Date(log.startDate), 'yyyy-MM-dd HH:mm')} -{' '}
                          {format(new Date(log.endDate), 'yyyy-MM-dd HH:mm')}
                        </p>
                      </div>

                      <div className="ml-auto font-medium text-right">
                        <div className="space-y-1">
                          <p>{log.job}</p>
                        </div>
                        <div className="flex gap-x-1">
                          {log.metric && (
                            <Badge variant="info" className="first-letter:uppercase">
                              {log.metric}
                            </Badge>
                          )}
                          <Badge variant="info">{upperCase(log.type)}</Badge>
                        </div>
                      </div>
                    </div>

                    <div className="mt-2">
                      <p className="text-sm text-red-600 dark:text-red-400 truncate mb-1 line-clamp-2">
                        {log.error}
                      </p>

                      <Dialog
                        title={`Error for log ID ${log._id}`}
                        trigger={
                          <Button appearance="light" size="small">
                            View error
                          </Button>
                        }
                        confirmButton={null}
                        cancelButton={null}
                      >
                        <div>Log Job ID: {log.job}</div>
                        <div>{log.error}</div>
                      </Dialog>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="border-b border-r border-gray-100 dark:border-border p-4 py-8">
          <div>
            <div className="flex flex-row flex-wrap justify-between items-start px-4 mb-2">
              <div className="leading-tight font-semibold text-gray-900 dark:text-foreground pt-[2px] text-sm">
                <div className="text-left mb-2">
                  <div>Completed logs</div>

                  <div className="font-normal mt-2">{totalCompletedLogs}</div>
                </div>
              </div>

              <div className="mb-auto">
                <Button
                  appearance="secondary"
                  size="small"
                  onClick={() =>
                    deleteStatisticLogsMutation.mutate({
                      status: 'completed',
                    })
                  }
                >
                  Delete completed logs
                </Button>
              </div>
            </div>

            <div className="py-3 px-4">
              <div className="overflow-auto" style={{ height: '384px' }}>
                {completedLogs?.map(log => (
                  <div className="flex h-14" key={log._id}>
                    <div className="space-y-1">
                      <p className="text-sm">
                        Seller: <Link to={`/admin/merchants/${log.seller}`}>{log.seller}</Link>
                      </p>
                      <p className="text-sm text-gray-700 dark:text-muted-foreground">
                        Date range: {format(new Date(log.startDate), 'yyyy-MM-dd HH:mm')} -{' '}
                        {format(new Date(log.endDate), 'yyyy-MM-dd HH:mm')}
                      </p>
                    </div>
                    <div className="ml-auto font-medium text-right">
                      <div className="space-y-1">
                        <p>{log.job}</p>
                      </div>
                      <div className="flex gap-x-1">
                        {log.metric && (
                          <Badge variant="info" className="first-letter:uppercase">
                            {log.metric}
                          </Badge>
                        )}
                        <Badge variant="info">{upperCase(log.type)}</Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
